import {Trans} from '@lingui/macro';
import {DateTime} from 'luxon';
import {ellipsis} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {CreateFormInputDisplay} from '../../../../../components/common/createFormInputs/createFormInputDisplay';
import {Skeleton} from '../../../../../components/common/skeleton';
import {formatDate} from '../../../../../helpers/dateTimeHelpers';
import {useClipboard} from '../../../../../helpers/hooks/clipboardHookHelpers';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {LocalApplicationModel} from '../../../../../models/api/localApplicationModel';
import {fetchLocalApplicationsAppSecretAsyncAction} from '../../../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../banners/settingsBannersContext';

/*
 * Constants.
 */

const hiddenAppSecretDisplay = '**********************************************';

/*
 * Props.
 */

interface SettingsDevelopersApplicationReadonlyFieldsProps {
  application: LocalApplicationModel;
}

/*
 * Style.
 */

const StyledFieldWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

const StyledSmallCreateFormInputDisplay = styled(CreateFormInputDisplay)`
  flex: 0 0 50%;
  max-width: 490px;
`;

const StyledSmallTextBox = styled(TextBox)`
  width: 230px;
`;

const StyledAppSecretWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

const StyledAppSecretTextBox = styled(TextBox)`
  flex: 0 0 395px;
  padding-right: 4px;
  ${ellipsis()};
`;

const StyledButton = styled(Button)`
  height: 30px;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledAppSecretCreateFormInputDisplay = styled(CreateFormInputDisplay)`
  flex: 0 0 100%;
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationReadonlyFields: FC<
  SettingsDevelopersApplicationReadonlyFieldsProps
> = (props) => {
  const {application} = props;
  const localize = useLocalize();
  const {showBanner} = useSettingsBanners();
  const {copy} = useClipboard();
  const dispatch = useAppDispatch();

  const [shouldRevealAppSecret, setShouldRevealAppSecret] = useState(false);
  const [isFetchingSecret, setIsFetchingSecret] = useState(false);
  const [appSecret, setAppSecret] = useState<string>();

  const fetchSecretAsync = async () => {
    try {
      setIsFetchingSecret(true);
      const secret = await dispatch(fetchLocalApplicationsAppSecretAsyncAction(application.id));
      setAppSecret(secret);
      return secret;
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="developers-application-info-app-secret-failed-to-fetch">
            Failed to fetch app secret.
          </Trans>
        ),
      });
      return undefined;
    } finally {
      setIsFetchingSecret(false);
    }
  };

  const getAppSecretAsync = async () => {
    if (appSecret) {
      return appSecret;
    }
    const secret = await fetchSecretAsync();
    return secret;
  };

  const onCopyClickAsync = async () => {
    const secret = await getAppSecretAsync();
    if (!secret) {
      return;
    }

    copy(secret);
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: (
        <Trans id="developers-application-info-app-secret-copied">Copied app secret to clipboard.</Trans>
      ),
    });
  };

  const onToggleReveal = () => {
    setShouldRevealAppSecret((v) => !v);
    getAppSecretAsync();
  };

  return (
    <StyledFieldWrapperDiv>
      <StyledSmallCreateFormInputDisplay
        title={<Trans id="developers-application-info-app-creator-title">App name</Trans>}
        isDisabled
        input={<StyledSmallTextBox defaultValue="Temp user" isDisabled noMargin />}
      />
      <StyledSmallCreateFormInputDisplay
        title={<Trans id="developers-application-info-app-creation-date-title">Creation date</Trans>}
        isDisabled
        input={
          <StyledSmallTextBox
            defaultValue={formatDate(
              localize,
              DateTime.fromMillis(application.createdAt),
              DateTime.DATE_FULL,
            )}
            isDisabled
            noMargin
          />
        }
      />
      <StyledSmallCreateFormInputDisplay
        title={<Trans id="developers-application-info-app-last-updated-title">Last updated</Trans>}
        isDisabled
        input={
          <StyledSmallTextBox
            defaultValue={formatDate(
              localize,
              DateTime.fromMillis(application.updatedAt),
              DateTime.DATE_FULL,
            )}
            isDisabled
            noMargin
          />
        }
      />
      <StyledSmallCreateFormInputDisplay
        title={<Trans id="developers-application-info-app-uid-title">App UID</Trans>}
        isDisabled
        input={<StyledSmallTextBox defaultValue={application.applicationUid} isDisabled noMargin />}
        tooltip={
          <Trans id="developers-application-info-app-uid-tooltip">
            Unique identifier for this app. When reaching out to Front&apos;s support team, please share this
            UID to help us resolve any issues.
          </Trans>
        }
      />
      <StyledAppSecretCreateFormInputDisplay
        title={<Trans id="developers-application-info-app-secret">App secret</Trans>}
        isDisabled
        input={
          <StyledAppSecretWrapperDiv>
            {!isFetchingSecret && (
              <StyledAppSecretTextBox
                value={shouldRevealAppSecret ? appSecret : hiddenAppSecretDisplay}
                isDisabled
                noMargin
              />
            )}
            {isFetchingSecret && <Skeleton width="395px" height="30px" />}
            <StyledButton onClick={onCopyClickAsync} isDisabled={isFetchingSecret}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name="copySmall" />
                <Trans id="developers-application-info-app-secret-copy">Copy</Trans>
              </StyledButtonContentWrapperDiv>
            </StyledButton>
            <StyledButton onClick={onToggleReveal} isDisabled={isFetchingSecret}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name={shouldRevealAppSecret ? 'eyeCrossedSmall' : 'eyeSmall'} />
                {!shouldRevealAppSecret && (
                  <Trans id="developers-application-info-app-secret-reveal">Reveal</Trans>
                )}
                {shouldRevealAppSecret && (
                  <Trans id="developers-application-info-app-secret-hide">Hide</Trans>
                )}
              </StyledButtonContentWrapperDiv>
            </StyledButton>
          </StyledAppSecretWrapperDiv>
        }
        tooltip={
          <Trans id="developers-application-info-app-secret-tooltip">
            The secret Front will use to sign requests sent to any webhooks your app has configured.
          </Trans>
        }
      />
    </StyledFieldWrapperDiv>
  );
};
